import { useMutation } from "@apollo/client";
import { Modal, Space, Text, Title } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useState } from "react";

import { STYLE } from "../types/constants";
import { UserType, OrderStatus } from "../types/enums";
import {
  getPatientLabelFirstLine,
  translatedPatientAgeAndSex,
} from "../utils/patient.utils";
import { getDentistCode, isUserProvider } from "../utils/user.utils";
import BoxOrderView from "./BoxOrderView";
import UpdatePatientModal from "./modals/updatePatientModal";

import { IntegrationTypeEnum } from "@jasper/client-models";
import {
  isImplant,
  isOrderWithNoAttachement,
  isPAC,
} from "../utils/product.utils";
import { UPDATE_PATIENT, CREATE_PATIENT } from "../gql/patients";
import { useNavigate } from "react-router-dom";
import { Order, Patient, User } from "../types/interfaces";
import { getExternalOrderLink } from "../utils/order.utils";

interface OrderViewPatientComponentProps {
  order: Order;
  user: User;
  refetch: () => void;
  id: string;
}

const OrderViewPatientComponent = ({
  order,
  user,
  refetch,
  id,
}: OrderViewPatientComponentProps) => {
  const [isModalUpdatePatientOpened, setIsModalUpdatePatientOpened] =
    useState<boolean>(false);
  const [updatePatient] = useMutation(UPDATE_PATIENT);
  const [createPatient] = useMutation(CREATE_PATIENT);

  const navigate = useNavigate();
  const externalOrderLink = getExternalOrderLink(order);

  return (
    <BoxOrderView id={id} span={5} minHeight={28}>
      <Modal
        opened={isModalUpdatePatientOpened}
        onClose={() => setIsModalUpdatePatientOpened(false)}
      >
        <UpdatePatientModal
          patient={order?.patient}
          onSubmit={async (values: Patient) => {
            try {
              if (order?.patient?.id) {
                await updatePatient({
                  variables: {
                    where: {
                      id: order?.patient?.id,
                    },
                    data: {
                      firstName: {
                        set: values.firstName,
                      },
                      lastName: {
                        set: values.lastName,
                      },
                    },
                  },
                });
              } else {
                await createPatient({
                  variables: {
                    args: {
                      firstName: values.firstName,
                      lastName: values.lastName,
                      dentist: {
                        connect: {
                          id: order.user.id,
                        },
                      },
                      order: {
                        connect: {
                          id: order.id,
                        },
                      },
                    },
                  },
                });
              }
              notifications.show({
                title: "Patient updated",
                color: "green",
                message: "",
              });
              refetch();
              setIsModalUpdatePatientOpened(false);
            } catch (e) {
              console.error(e);
              notifications.show({
                title: "Error while trying to update patient",
                color: "red",
                message: "",
              });
            }
          }}
        />
      </Modal>
      <Title
        order={5}
        style={{
          color: STYLE.primary,
        }}
      >
        Order {order.orderReference}
      </Title>
      {externalOrderLink && (
        <Text size="sm">
          <a
            href={externalOrderLink.url}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: STYLE.primary }}
          >
            {externalOrderLink.label}
          </a>
        </Text>
      )}
      <Space h="md" />
      <Title order={5}>Patient</Title>
      <Text>
        {getPatientLabelFirstLine(order)}
        {!isUserProvider(user) && (
          <span
            style={{
              marginLeft: "2px",
              cursor: "pointer",
              textDecoration: "underline",
            }}
            onClick={() => setIsModalUpdatePatientOpened(true)}
          >
            (modifier)
          </span>
        )}
      </Text>
      <Text>{translatedPatientAgeAndSex(order?.patient)}</Text>
      <Space h="md" />
      <Title order={5}>Doctor</Title>
      {user.type !== UserType.SUPPLIER ? (
        <Text>
          <span
            onClick={() => navigate(`/users/${order?.user?.id}`)}
            style={{ cursor: "pointer", textDecoration: "underline" }}
          >
            {`${order?.user?.firstName ?? ""} ${order?.user?.lastName ?? ""}`}
          </span>
          <span> - </span>
          <span
            onClick={() => {
              if (!order?.user?.userGroup?.id) {
                notifications.show({
                  title: "User is not member of a group",
                  color: "red",
                  message: "",
                });
                return;
              }
              navigate(`/groups/${order?.user?.userGroup?.id}`);
            }}
            style={{ cursor: "pointer", textDecoration: "underline" }}
          >
            {`${order?.user?.userGroup?.clinicNumber}`}
          </span>
        </Text>
      ) : (
        <>
          <Text>
            Doctor code : {getDentistCode(order?.user)} <br />
          </Text>
          <Text>
            Clinic code :{" "}
            {order?.user?.userGroup?.clinicNumber ?? "NOT_PROVIDED"}
          </Text>
        </>
      )}
      {!isUserProvider(user) &&
        (order?.user?.phoneNumber || order?.user?.userGroup?.phoneNumber) && (
          <div style={{ paddingTop: "1rem" }}>
            <Title order={5}>Contact</Title>
            {order?.user?.phoneNumber && (
              <Text>Doctor: {order?.user?.phoneNumber}</Text>
            )}
            {order?.user?.userGroup?.phoneNumber && (
              <Text>Clinic: {order?.user?.userGroup?.phoneNumber}</Text>
            )}
          </div>
        )}
      {!isUserProvider(user) && order?.user?.userGroup?.description && (
        <div style={{ paddingTop: "1rem" }}>
          <Title order={5}>Clinic info</Title>
          <Text style={{ whiteSpace: "pre-line" }}>
            {order.user.userGroup.description}
          </Text>
        </div>
      )}
      <Space h="md" />
      {!isUserProvider(user) && (
        <div style={{ color: "#ff5d00", fontWeight: "bold" }}>
          {(order?.isRush ?? false) && "RUSH "}
          {(order?.isPhysicalPrint ?? false) && "PHYSICAL-PRINT "}
          {(order?.orderComment ?? []).length > 0 && " COMMENT "}
          {isImplant(order) && "IMPLANT "}
          {isOrderWithNoAttachement(order) && "NO-ATTACHEMENT "}
          {order.status === OrderStatus.NEED_TO_BE_PROCESSED &&
            order.integrationType === IntegrationTypeEnum.THREESHAPE &&
            "COMMUNICATE"}
          {isPAC(order) && "PAC "}
          {(order?.redo ?? []).length > 0 && "REDO"}
        </div>
      )}
    </BoxOrderView>
  );
};

export default OrderViewPatientComponent;
